import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, Smile, Frown, Meh, Battery, Zap, CheckCircle } from 'lucide-react';
import * as planService from '../../../services/planService';

/**
 * Check-In Form Component
 * Weekly check-in with mood, energy, and adherence tracking
 */
export default function CheckInForm({ planId }) {
  const [checkIns, setCheckIns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    mood: 5, // 1-10 scale
    energy: 5, // 1-10 scale
    adherence: 5, // 1-10 scale
    workoutAdherence: 5,
    nutritionAdherence: 5,
    sleep: 7, // hours
    stress: 5, // 1-10 scale
    notes: '',
  });

  useEffect(() => {
    if (planId) {
      loadCheckIns();
    }
  }, [planId]);

  const loadCheckIns = async () => {
    setIsLoading(true);
    try {
      const result = await planService.getProgress(planId);
      if (result.success) {
        const checkInEntries = (result.data.entries || []).filter(
          (e) => e.data?.check_in === true
        );
        setCheckIns(checkInEntries.sort((a, b) => 
          new Date(b.entry_date) - new Date(a.entry_date)
        ));
      }
    } catch (error) {
      console.error('Error loading check-ins:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await planService.submitCheckIn(planId, {
        date: formData.date,
        mood: formData.mood,
        energy: formData.energy,
        adherence: formData.adherence,
        workoutAdherence: formData.workoutAdherence,
        nutritionAdherence: formData.nutritionAdherence,
        sleep: formData.sleep,
        stress: formData.stress,
        notes: formData.notes,
      });

      if (result.success) {
        setFormData({
          date: new Date().toISOString().split('T')[0],
          mood: 5,
          energy: 5,
          adherence: 5,
          workoutAdherence: 5,
          nutritionAdherence: 5,
          sleep: 7,
          stress: 5,
          notes: '',
        });
        loadCheckIns();
      }
    } catch (error) {
      console.error('Error submitting check-in:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getMoodIcon = (mood) => {
    if (mood >= 8) return <Smile className="w-6 h-6 text-green-600" />;
    if (mood >= 5) return <Meh className="w-6 h-6 text-yellow-600" />;
    return <Frown className="w-6 h-6 text-red-600" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading check-ins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Weekly Check-In</h2>
      </div>

      {/* Check-In Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-6"
      >
        <h3 className="text-xl font-bold text-gray-900 mb-6">Submit Check-In</h3>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              required
            />
          </div>

          {/* Mood Scale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Mood (1-10)
            </label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-16">Poor</span>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.mood}
                onChange={(e) => setFormData({ ...formData, mood: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-16">Great</span>
              <div className="w-12 text-center">
                {getMoodIcon(formData.mood)}
                <span className="text-lg font-bold text-gray-900">{formData.mood}</span>
              </div>
            </div>
          </div>

          {/* Energy Scale */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Energy Level (1-10)
            </label>
            <div className="flex items-center gap-4">
              <Battery className="w-5 h-5 text-gray-400" />
              <input
                type="range"
                min="1"
                max="10"
                value={formData.energy}
                onChange={(e) => setFormData({ ...formData, energy: parseInt(e.target.value) })}
                className="flex-1"
              />
              <Zap className="w-5 h-5 text-yellow-500" />
              <span className="text-lg font-bold text-gray-900 w-12 text-center">
                {formData.energy}
              </span>
            </div>
          </div>

          {/* Overall Adherence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Overall Adherence (1-10)
            </label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-16">Low</span>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.adherence}
                onChange={(e) => setFormData({ ...formData, adherence: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-16">Perfect</span>
              <span className="text-lg font-bold text-gray-900 w-12 text-center">
                {formData.adherence}
              </span>
            </div>
          </div>

          {/* Workout Adherence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Workout Adherence (1-10)
            </label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-16">Missed</span>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.workoutAdherence}
                onChange={(e) => setFormData({ ...formData, workoutAdherence: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-16">All Done</span>
              <span className="text-lg font-bold text-gray-900 w-12 text-center">
                {formData.workoutAdherence}
              </span>
            </div>
          </div>

          {/* Nutrition Adherence */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Nutrition Adherence (1-10)
            </label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-16">Off Plan</span>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.nutritionAdherence}
                onChange={(e) => setFormData({ ...formData, nutritionAdherence: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-16">Perfect</span>
              <span className="text-lg font-bold text-gray-900 w-12 text-center">
                {formData.nutritionAdherence}
              </span>
            </div>
          </div>

          {/* Sleep */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Sleep (hours)
            </label>
            <input
              type="number"
              min="0"
              max="24"
              step="0.5"
              value={formData.sleep}
              onChange={(e) => setFormData({ ...formData, sleep: parseFloat(e.target.value) })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>

          {/* Stress Level */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Stress Level (1-10)
            </label>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 w-16">Low</span>
              <input
                type="range"
                min="1"
                max="10"
                value={formData.stress}
                onChange={(e) => setFormData({ ...formData, stress: parseInt(e.target.value) })}
                className="flex-1"
              />
              <span className="text-sm text-gray-600 w-16">High</span>
              <span className="text-lg font-bold text-gray-900 w-12 text-center">
                {formData.stress}
              </span>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={4}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="How are you feeling? Any challenges or wins this week?"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Check-In
              </>
            )}
          </button>
        </form>
      </motion.div>

      {/* Check-In History */}
      {checkIns.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Check-In History</h3>
          <div className="space-y-4">
            {checkIns.map((checkIn) => {
              const data = checkIn.data || {};
              return (
                <div key={checkIn.id} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">
                      {new Date(checkIn.entry_date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </h4>
                    {getMoodIcon(data.mood || 5)}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Mood</p>
                      <p className="font-medium text-gray-900">{data.mood || 'N/A'}/10</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Energy</p>
                      <p className="font-medium text-gray-900">{data.energy || 'N/A'}/10</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Adherence</p>
                      <p className="font-medium text-gray-900">{data.adherence || 'N/A'}/10</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Sleep</p>
                      <p className="font-medium text-gray-900">{data.sleep || 'N/A'} hrs</p>
                    </div>
                  </div>
                  {checkIn.notes && (
                    <p className="text-sm text-gray-600">{checkIn.notes}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
